import _ from 'lodash';
import { ReactNode, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '../../../../Styles/themed-styled-components';

import { FieldGenerator, RenderContext, StyleVariant } from '@breathelife/field-generator';
import {
  getRepeatedFieldGroupIndex,
  isRenderingRepeatedFieldGroup,
  RenderingFieldGroup,
} from '@zinnia/form-engine-sdk';

import { SubmitButton } from '../../../../Components/Button/SubmitButton';
import { useCarrierContext, useLanguage } from '../../../../Hooks';
import { useAssistedApplicationContext } from '../../../../Hooks/useAssistedApplicationContext';
import { ModalLayout } from '../../../../Layouts/Modal/ModalLayout';
import { useViewNextIncompleteFieldContext } from '../../AssistedApplicationView/ViewNextIncompleteFieldContext';

type FieldGroupCardModalProps = {
  isOpen: boolean;
  onCloseModal: () => void;
  secondaryAction?: ReactNode;
  fieldGroup: RenderingFieldGroup;
};

const modalLayoutBodyStyling = css`
  overflow-y: auto;
  max-height: 500px;
`;

export function FieldGroupCardModal(props: FieldGroupCardModalProps): ReactElement {
  const { isOpen, onCloseModal, secondaryAction, fieldGroup } = props;
  const { iconMap } = useCarrierContext();
  const language = useLanguage();
  const { t } = useTranslation();
  const { onAnswerChange, onAnswerComplete } = useAssistedApplicationContext();

  const { focusedIncompleteFieldId } = useViewNextIncompleteFieldContext();

  /**
   * We already display the question's title as the modal's title, hence we don't want to
   * display it again in the modal's content and can remove it
   */
  const fieldGeneratorProps = {
    context: RenderContext.web,
    styleVariant: StyleVariant.pro,
    iconMap,
    locale: language,
    fieldGroup: _.omit(fieldGroup, 'title'),
    onAnswerChange,
    onAnswerComplete,
    focusedIncompleteFieldId,
  };

  /**
   * /!\ IMPORTANT /!\
   * In order to get the repeatedInstanceIdentifierContext to account for repeated questions and multi-insured,
   * we want to handle the UI logic here instead of letting FieldGenerator do it.
   * The only issue with that is that we lose the repeatedQuestionIndex in the answer
   * complete callback added by FieldGenerator, hence the repeated question flag to manually add it.
   */

  fieldGeneratorProps.onAnswerChange = (items) => {
    const overrideAnswers = items.map((item) => ({
      ...item,
      blueprintIdScope: fieldGroup.scope?.repeatedInstanceIdentifierContext.byBlueprintId || {},
      nodeIdScope: fieldGroup.scope?.repeatedInstanceIdentifierContext.byNodeId || {},
    }));
    onAnswerChange(overrideAnswers);
  };

  if (isRenderingRepeatedFieldGroup(fieldGroup)) {
    const repeatedFieldGroupIndex = getRepeatedFieldGroupIndex(fieldGroup);

    fieldGeneratorProps.fieldGroup = _.omit(fieldGeneratorProps.fieldGroup, 'options.repeatable');
    fieldGeneratorProps.onAnswerComplete = (fieldId, answer, previousAnswer) =>
      onAnswerComplete(fieldId, answer, previousAnswer, repeatedFieldGroupIndex);
  }

  const modalLabelProps = fieldGroup.title
    ? { title: fieldGroup.title }
    : { ariaLabel: t('modals.assistedApplication.fieldCard.fallbackTitle', { id: fieldGroup.id }) };

  return (
    <ModalLayout
      hideCancel
      maxWidth='sm'
      isOpen={isOpen}
      closeModal={onCloseModal}
      secondaryAction={secondaryAction}
      submitButton={
        <SubmitButton onClick={onCloseModal} data-testid={`${fieldGroup.id}-save`}>
          {t('cta.close')}
        </SubmitButton>
      }
      bodyStyling={modalLayoutBodyStyling}
      {...modalLabelProps}
    >
      <FieldGenerator {...fieldGeneratorProps} />
    </ModalLayout>
  );
}
